<template>
  <div :style="divStyles">
    <img :src="svgComponent" :style="primaryStyles" />
    <img
      v-if="competitive"
      class="secondary-icon"
      :src="secondaryComponent"
      :style="secondaryStyles"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import AudienceNetworkSvg from '@/assets/images/channels/audience-network.svg';
import BlueskySvg from '@/assets/images/channels/bluesky.svg';
import BrandhealthSvg from '@/assets/images/channels/brandhealth.svg';
import CompetitiveSvg from '@/assets/images/channels/competitive.svg';
import FacebookAdsSvg from '@/assets/images/channels/facebook-ads.svg';
import FacebookSvg from '@/assets/images/channels/facebook-light.svg';
import GoogleAnalyticsSvg from '@/assets/images/channels/ga-with-border.svg';
import InstagramSvg from '@/assets/images/channels/instagram.svg';
import InstagramStoriesSvg from '@/assets/images/channels/instagram-stories.svg';
import LinkedInSvg from '@/assets/images/channels/linkedin.svg';
import MessengerSvg from '@/assets/images/channels/messenger.svg';
import MetaSvg from '@/assets/images/channels/meta.svg';
import PinterestSvg from '@/assets/images/channels/pinterest.svg';
import QuoraSvg from '@/assets/images/channels/quora.svg';
import RedditSvg from '@/assets/images/channels/reddit.svg';
import SnapchatSvg from '@/assets/images/channels/snapchat.svg';
import ThreadsSvg from '@/assets/images/channels/threads.svg';
import TikTokSvg from '@/assets/images/channels/tiktok.svg';
import TwitterSvg from '@/assets/images/channels/twitter.svg';
import WebSvg from '@/assets/images/channels/web.svg';
import YoutubeSvg from '@/assets/images/channels/youtube.svg';
import CanvaSvg from '@/assets/images/integrations/canva.svg';

const SIZE_MAP = {
  tiny: '0.5',
  xxxsmall: '0.625',
  xxsmall: '0.75',
  xsmall: '1',
  small: '1.25',
  medium: '1.5',
  large: '1.75',
  xlarge: '2',
  xxlarge: '3',
  xxxlarge: '4',
  xxxxlarge: '8',
};

const ICONS = {
  brandhealth: BrandhealthSvg,
  competitive: CompetitiveSvg,
  instagram: InstagramSvg,
  instagramStories: InstagramStoriesSvg,
  instagram_stories: InstagramStoriesSvg,
  facebook: FacebookSvg,
  facebook_ads: FacebookAdsSvg,
  pinterest: PinterestSvg,
  pinterest_v5: PinterestSvg,
  tiktok: TikTokSvg,
  tiktok_ads: TikTokSvg,
  tiktok_creator_marketplace: TikTokSvg,
  twitter: TwitterSvg,
  youtube: YoutubeSvg,
  ga: GoogleAnalyticsSvg,
  reddit: RedditSvg,
  quora: QuoraSvg,
  web: WebSvg,
  linkedin: LinkedInSvg,
  meta: MetaSvg,
  messenger: MessengerSvg,
  audience_network: AudienceNetworkSvg,
  x: TwitterSvg,
  threads: ThreadsSvg,
  bluesky: BlueskySvg,
  snapchat: SnapchatSvg,
  canva: CanvaSvg,
};

const comp = defineComponent({
  props: {
    value: {
      type: String,
      default: null,
      validator(value) {
        if (value === null) {
          return true;
        }
        return Object.keys(ICONS).indexOf(value) !== -1;
      },
    },
    /**
     * Tiny size.
     */
    tiny: { type: Boolean, default: false },
    /**
     * xxxsmall size.
     */
    xxxsmall: { type: Boolean, default: false },
    /**
     * xxsmall size.
     */
    xxsmall: { type: Boolean, default: false },
    /**
     * xmsmall size.
     */
    xsmall: { type: Boolean, default: false },
    /**
     * small size.
     */
    small: { type: Boolean, default: false },
    /**
     * medium size.
     */
    medium: { type: Boolean, default: false },
    /**
     * large size.
     */
    large: { type: Boolean, default: false },
    /**
     * xlarge size.
     */
    xlarge: { type: Boolean, default: false },
    /**
     * xxlarge size.
     */
    xxlarge: { type: Boolean, default: false },
    /**
     * xxxlarge size.
     */
    xxxlarge: { type: Boolean, default: false },
    /**
     * xxxxlarge size.
     */
    xxxxlarge: { type: Boolean, default: false },
    competitive: { type: Boolean, default: false },
  },
  computed: {
    svgComponent() {
      if (this.competitive) {
        return ICONS.competitive;
      }
      return ICONS[this.value];
    },
    secondaryComponent() {
      return ICONS[this.value];
    },
    size() {
      const {
        tiny,
        xxxsmall,
        xxsmall,
        xsmall,
        small,
        medium,
        large,
        xlarge,
        xxlarge,
        xxxlarge,
        xxxxlarge,
      } = this.$props;
      const sizes = {
        tiny,
        xxxsmall,
        xxsmall,
        xsmall,
        small,
        medium,
        large,
        xlarge,
        xxlarge,
        xxxlarge,
        xxxxlarge,
      };
      const size = Object.keys(sizes).find((key) => sizes[key]) || 'medium';
      return SIZE_MAP[size];
    },
    divStyles() {
      const fullSize = Number(this.size);
      return {
        height: `${fullSize}rem`,
        position: 'relative',
      };
    },
    primaryStyles() {
      return {
        height: `${this.size}rem`,
        width: `${this.size}rem`,
      };
    },
    secondaryStyles() {
      const fullSize = Number(this.size);
      const secondarySize = Math.round(fullSize * 0.6);
      const leftOffset = Math.round(fullSize * 0.8);
      return {
        height: `${secondarySize}rem`,
        width: `${secondarySize}rem`,
        position: 'absolute',
        bottom: 0,
        left: `${leftOffset}rem`,
      };
    },
  },
});
export default comp;
</script>
